import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { ArrowRightIcon, BookOpenIcon, PlusIcon, XIcon } from '../Icons';

interface AddNovelSheetProps {
  isOpen: boolean;
  isDark: boolean;
  onClose: () => void;
  onChooseFile: () => void;
  onChooseNarou: () => void;
}

export function AddNovelSheet({
  isOpen,
  isDark,
  onClose,
  onChooseFile,
  onChooseNarou,
}: AddNovelSheetProps) {
  const sheetRef = useRef<HTMLDialogElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !sheetRef.current) return;

      const focusable = Array.from(
        sheetRef.current.querySelectorAll<HTMLElement>('button:not([disabled])'),
      );
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      previousFocusRef.current?.focus();
    };
  }, [isOpen, onClose]);

  const surfaceClass = isDark
    ? 'border-white/10 bg-[#111114] text-[#FAF8F5]'
    : 'border-black/10 bg-[#FAF8F5] text-[#111111]';
  const optionClass = isDark
    ? 'border-white/10 bg-white/5 hover:bg-white/10'
    : 'border-black/10 bg-white hover:bg-black/5';

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center">
          <button
            type="button"
            aria-label="追加メニューを閉じる"
            onClick={onClose}
            className="absolute inset-0 bg-black/45 backdrop-blur-sm"
          />
          <motion.dialog
            ref={sheetRef}
            open
            aria-modal="true"
            aria-labelledby="add-novel-title"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 280 }}
            className={`relative m-0 w-full max-w-lg rounded-t-3xl border-t px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-3 shadow-2xl ${surfaceClass}`}
          >
            <div className="mx-auto mb-4 h-1.5 w-11 rounded-full bg-neutral-500/30" />
            <div className="mb-4 flex items-center justify-between border-b border-current/10 pb-3">
              <h2 id="add-novel-title" className="font-serif text-lg font-bold">
                小説を追加
              </h2>
              <button
                ref={closeButtonRef}
                type="button"
                onClick={onClose}
                aria-label="閉じる"
                className="flex h-11 w-11 items-center justify-center rounded-xl transition-colors hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:hover:bg-white/10"
              >
                <XIcon size={18} />
              </button>
            </div>

            <div className="space-y-3">
              <button
                type="button"
                onClick={onChooseFile}
                className={`flex min-h-[76px] w-full items-center gap-4 rounded-2xl border p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${optionClass}`}
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 text-amber-700 dark:text-amber-300">
                  <PlusIcon size={22} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-bold">端末からファイルを追加</span>
                  <span className="mt-1 block text-xs opacity-65">
                    Markdown・テキスト（.md / .txt）
                  </span>
                </span>
                <ArrowRightIcon size={18} className="shrink-0 opacity-50" />
              </button>

              <button
                type="button"
                onClick={onChooseNarou}
                className={`flex min-h-[76px] w-full items-center gap-4 rounded-2xl border p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${optionClass}`}
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sky-500/10 text-sky-700 dark:text-sky-300">
                  <BookOpenIcon size={21} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-bold">なろう作品を追加</span>
                  <span className="mt-1 block text-xs opacity-65">
                    作品URLまたはNコードから読み込み
                  </span>
                </span>
                <ArrowRightIcon size={18} className="shrink-0 opacity-50" />
              </button>
            </div>
          </motion.dialog>
        </div>
      )}
    </AnimatePresence>
  );
}
